import { useCallback } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import type { ApiErrorResponse } from '../common/ApiClient';
import type { UseApiMutationOptions } from '../common/queryClient/createApiMutationHook';
import { useConfiguration } from '../configuration';
import { useVisualValidationGeneratingWindow } from './useVisualValidationGeneratingWindow';
import { useAlertContext } from '../common/form';
import translations from './useVisualValidationDashboardActions.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { GetApiErrorMessage } from '../common/utils/ValidationMessagesMap';
import { DashboardType } from './types';

const { addTranslations, t, useTranslation } = i18nNsFactory(
  'use-visual-validation-dashboard-actions',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t(`alert.error.code.${code}`, meta?.args && { ...meta.args });

const hasMissingRequestValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
};
type DashboardId = { externalCode: string; dashboardType?: DashboardType };

async function loadDashboardWindow<RequestData = any>(
  createAsync: (request: RequestData) => Promise<DashboardId>,
  openWindow: (generateAsync: () => Promise<DashboardId>) => void,
  setError: (message?: string, description?: any, timeout?: number) => void,
  setLoading: (loading: boolean) => void,
  validRequestData: RequestData,
  onSuccess?: () => Promise<any>,
) {
  let data: DashboardId | null = null;
  try {
    setLoading(true);
    data = await createAsync(validRequestData);
  } catch (error: any) {
    const apiError = (error.response?.data.errors ?? [])[0];
    if (apiError) {
      setError(translateMutationError(apiError.code, apiError.meta));
    }
  } finally {
    setLoading(false);
  }
  if (data) {
    openWindow(async () => {
      void onSuccess?.();
      return data;
    });
  }
}

type UseDashboardMutation<RequestData = Record<string, any>> = (
  options?: UseApiMutationOptions<DashboardId, RequestData>,
) => UseMutationResult<DashboardId, ApiErrorResponse, RequestData>;

export interface VisualValidationDashboardActionsProps<RequestData = any> {
  hasAccess: boolean;
  popupWindowPath: string;
  getRequestData: () => RequestData;
  useCreate: UseDashboardMutation<RequestData>;
  useDuplicate: UseDashboardMutation<RequestData & DashboardId>;
  useChangeData: UseDashboardMutation<RequestData & DashboardId>;
  onValidationFailed?: () => void;
  onSuccess?: () => Promise<any>;
  setLoading?: (loading: boolean) => void;
  getDashboardType?: (requestData: RequestData) => DashboardType;
}

export const useVisualValidationDashboardActions = <
  RequestData extends Record<string, any>,
>({
  hasAccess,
  popupWindowPath,
  getRequestData,
  useCreate,
  useDuplicate,
  useChangeData,
  onValidationFailed,
  onSuccess,
  setLoading,
  getDashboardType,
}: VisualValidationDashboardActionsProps<RequestData>) => {
  const { t } = useTranslation();
  const { setError } = useAlertContext();

  const openWindow = useVisualValidationGeneratingWindow(popupWindowPath);

  const getValidRequestData = useCallback(() => {
    const requestData = getRequestData();

    if (Object.values(requestData).some(hasMissingRequestValue)) {
      void onValidationFailed?.();
      return null;
    }

    return requestData;
  }, [getRequestData, onValidationFailed]);

  const createAsync = useCreate(doNotThrow).mutateAsync;
  const createDashboard = useCallback(async () => {
    if (!hasAccess) {
      setError(t('permission-denied'));
      return;
    }

    const validRequestData = getValidRequestData();
    if (validRequestData) {
      if (setLoading) {
        await loadDashboardWindow(
          createAsync,
          openWindow,
          setError,
          setLoading,
          validRequestData,
          onSuccess,
        );
      } else {
        openWindow(() =>
          createAsync(validRequestData).then((data) => {
            void onSuccess?.();
            return data;
          }),
        );
      }
    }
  }, [
    hasAccess,
    getValidRequestData,
    setError,
    t,
    openWindow,
    createAsync,
    onSuccess,
    setLoading,
  ]);

  const config = useConfiguration();
  const openDashboard = useCallback(
    (externalCode: string) => {
      if (!hasAccess) {
        setError(t('permission-denied'));
        return;
      }

      window.open(
        `${config.supersetUrl}/superset/dashboard/${externalCode}`,
        '_blank',
      );
    },
    [config.supersetUrl, hasAccess, setError, t],
  );

  const validateDashboardType = useCallback(
    (validRequestData: RequestData, dashboardType?: DashboardType) => {
      const expectedDashboardType = getDashboardType?.(validRequestData);
      if (
        dashboardType &&
        expectedDashboardType &&
        dashboardType !== expectedDashboardType
      ) {
        setError(t('invalid-dashboard-type'));
        return false;
      }
      return true;
    },
    [getDashboardType, setError, t],
  );

  const duplicateAsync = useDuplicate(doNotThrow).mutateAsync;
  const duplicateDashboard = useCallback(
    (externalCode: string, dashboardType?: DashboardType) => {
      if (!hasAccess) {
        setError(t('permission-denied'));
        return;
      }

      const validRequestData = getValidRequestData();
      if (
        validRequestData &&
        validateDashboardType(validRequestData, dashboardType)
      ) {
        openWindow(() =>
          duplicateAsync({ externalCode, ...validRequestData }).then((data) => {
            void onSuccess?.();
            return data;
          }),
        );
      }
    },
    [
      hasAccess,
      getValidRequestData,
      setError,
      t,
      openWindow,
      duplicateAsync,
      onSuccess,
      validateDashboardType,
    ],
  );

  const changeDataAsync = useChangeData(doNotThrow).mutateAsync;
  const changeDashboardData = useCallback(
    (externalCode: string, dashboardType?: DashboardType) => {
      if (!hasAccess) {
        setError(t('permission-denied'));
        return;
      }

      const validRequestData = getValidRequestData();
      if (
        validRequestData &&
        validateDashboardType(validRequestData, dashboardType)
      ) {
        openWindow(() =>
          changeDataAsync({ externalCode, ...validRequestData }).then(
            (data) => {
              void onSuccess?.();
              return data;
            },
          ),
        );
      }
    },
    [
      hasAccess,
      getValidRequestData,
      setError,
      t,
      openWindow,
      changeDataAsync,
      onSuccess,
      validateDashboardType,
    ],
  );

  return {
    createDashboard,
    openDashboard,
    duplicateDashboard,
    changeDashboardData,
  };
};

const doNotThrow = { throwOnError: false };
